import type { LoaderConfig, LoaderMetrics, TemplateConfig, TemplateSource   } from '@alekstar79/template-loader'
import { TemplateCache, TemplateLoader, TemplateRegistry } from '@alekstar79/template-loader'

let globalTemplateLoader: TemplateLoader

/**
 * Create a fully functional loader for the demo
 */
export async function createDemoLoader(
  fallbackTemplates: { name: string; source: string }[] = []
): Promise<TemplateLoader> {
  const registry = new TemplateRegistry()

  let templates: Record<string, TemplateSource> = {}

  try {
    // @ts-ignore
    templates = import.meta.glob<string>(
      '../demo/templates/*.html',
      {
        import: 'default',
        query: '?raw',
        eager: false
      }
    )

    // Converting for registration
    const sources: Record<string, TemplateSource> = {}
    Object.entries(templates)
      .forEach(([path, loader]) => {
        const name = path.split('/').pop()?.replace(/\.html$/, '')

        if (name) {
          sources[name] = async () => {
            if (typeof loader === 'function') {
              return await loader()
            }
            return String(loader)
          }
        }
      })

    await registry.registerBulk(sources)

  } catch (error) {
    console.error('[TemplateLoader] Failed to load templates:', error)

    // Minimal fallback templates in case of an error
    fallbackTemplates.forEach(({ name, source }) => {
      registry.register({ name, source })
    })
  }

  return new TemplateLoader(registry, {
    environment: 'development',
    cache: true,
    cacheTTL: 3600000,
    enableMetrics: true,
    fallbackTemplate: '<div class="template-error">Template not found</div>',
    onError: (error) => console.error(`[TemplateLoader Error] ${error.templateName}:`, error.message),
    onWarn: (message) => console.warn(`[TemplateLoader] ${message}`)
  })
}

/**
 * Create a loader for the library
 */
export function createLibraryLoader(): TemplateLoader & { registry: TemplateRegistry }
{
  const registry = new TemplateRegistry()

  return new TemplateLoader(registry, {
    environment: 'production',
    cache: true,
    cacheTTL: 3600000,
    enableMetrics: false,
    onWarn: (message) => console.warn(`[TemplateLoader] ${message}`),
    onError: (error) => {
      console.warn(`Template load failed: ${error.templateName}`)
    }
  })
}

/**
 * System initialization (called in main.ts or app initialization)
 */
export async function initializeTemplateSystem(): Promise<TemplateLoader>
{
  if (globalTemplateLoader) return globalTemplateLoader

  const isDevMode =
    // @ts-ignore - Vite environment variable
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) ||
    (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development')

  if (isDevMode) {
    globalTemplateLoader = await createDemoLoader()
  } else {
    globalTemplateLoader = createLibraryLoader()

    globalTemplateLoader.registry.register({
      name: 'media',
      source: async () => {
        const res = await fetch('./templates/media.html')
        return res.text()
      },
      metadata: {
        version: '1.0',
        description: 'Media card',
        cached: true
      }
    })

    globalTemplateLoader.registry.register({
      name: 'userProfile',
      source: '<div class="user-profile"><h2>Profile</h2></div>',
      metadata: {
        version: '1.0',
        description: 'User profile card',
        cached: true
      }
    })
  }

  return globalTemplateLoader
}

/**
 * Get a global instance
 */
export function getTemplateLoader(): TemplateLoader
{
  if (!globalTemplateLoader) {
    throw new Error('Template system not initialized. Call initializeTemplateSystem() first.')
  }

  return globalTemplateLoader
}

export {
  LoaderConfig,
  LoaderMetrics,
  TemplateConfig,
  TemplateSource,
  TemplateCache,
  TemplateLoader,
  TemplateRegistry
}
